import React, {useMemo} from 'react';
import {
  Breakpoint,
  Dialog,
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
  DialogContentProps,
  DialogProps,
  IconButton,
  Paper,
  PaperProps,
  styled,
} from '@mui/material';
import MuiDialogTitle from '@mui/material/DialogTitle';
import {CloseIcon} from '../../controls/MaterialUIIcons';
import {ButtonTypography, GreyOutlinedButton, PrimaryContainedButton} from '../Buttons/buttons';
import Draggable from 'react-draggable';
import {useDialogStyles, usePaperStyles, useResizeStyles} from './stylesDialog';
import {ResizableBox} from 'react-resizable';
import {useTheme} from '@mui/styles';
import clsx from 'clsx';

type EditDialogProps = DialogProps & {
  open: boolean;
  title?: string;
  editorModus: EditorModus;
  canSave: boolean;
  onSave: () => void;
  onCancel: () => void;
  maxHeight?: false | Breakpoint | undefined;
  children: React.ReactNode;
  hasCloseBtn?: boolean;
  registerNavn?: string;
  testId?: string;
  contentProps?: DialogContentProps;
  lagreBtnTitle?: string;
  width?: number;
  height?: number;
  hideSaveBtn?: boolean;
};

export enum EditorModus {
  Endre, // update
  Opprett, // create
  OpprettBarn, // create
  Kopi, // copy
  Soek, // search
  Vis, // show
  Slett, // delete
  LåsTab, // lock tab
  IkkeNoen, //none
  Logg, //log
}

export const EditDialogTitle = styled(MuiDialogTitle)(({theme}) => ({
  backgroundColor: theme.palette.primary.main,
  fontWeight: 'bold',
  fontSize: '16px',
  paddingTop: 11,
  paddingBottom: 11,
  color: 'white',
  paddingLeft: '1.5rem',
  paddingRight: '1.5rem',
  marginBottom: 15
}));

export const DialogActions = styled(MuiDialogActions)(({theme}) => ({
  marginBottom: '1.25rem',
  marginRight: '1.25rem'
}));

export const DialogContent = styled(MuiDialogContent)(({theme}) => ({
  paddingLeft: '1.75rem',
  paddingRight: '1.75rem',
  paddingBottom: '0.75rem'
}));

export function PaperComponent(props: PaperProps) {
  const {paper: paperStyle} = usePaperStyles();
  return (
    <Draggable
      handle='#draggable-dialog-title'
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} className={clsx(props.className, paperStyle)} />
    </Draggable>
  );
}

export const EditDialog = (props: EditDialogProps) => {
  const {
    open,
    editorModus,
    registerNavn,
    canSave,
    title,
    onSave,
    testId = '',
    onCancel,
    maxWidth,
    maxHeight,
    children,
    hasCloseBtn = false,
    lagreBtnTitle = 'Lagre',
    contentProps,
    hideSaveBtn,
    ...other
  } = props;
  const {breakpoints} = useTheme();
  const defaultHeight = useMemo(() => {
    if (maxHeight) {
      return breakpoints.values[maxHeight];
    }
    return 400;
  }, [maxHeight,breakpoints.values]);
  const defaultWidth = useMemo(() => {
    if (maxWidth) {
      return breakpoints.values[maxWidth];
    }
    return 600;
  }, [breakpoints.values, maxWidth]);
  const {height = defaultHeight, width = defaultWidth} = other;

  function onClose(event: {}, reason: 'backdropClick' | 'escapeKeyDown') {
    if (reason && reason === 'backdropClick') return;
    onCancel();
  }
  const {dialogContent : dialogContentStyles} = useDialogStyles();
  const {resizable} = useResizeStyles();
  return (
    <>
      <Dialog
        {...other}
        open={open}
        keepMounted
        onClose={onClose}
        PaperComponent={PaperComponent}
        aria-labelledby='draggable-dialog-title'
      >
        <ResizableBox
          height={height}
          width={width}
          className={resizable}
        >
          <>
            <EditDialogTitle
              sx={hasCloseBtn ? {m: 0, p: 2} : undefined}
              style={{fontSize: '16px', paddingTop: 11, paddingBottom: 11}}
              id='draggable-dialog-title'
            >
              {title}
              {hasCloseBtn && (
                <IconButton
                  aria-label='close'
                  onClick={onCancel}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                  }}
                >
                  <CloseIcon sx={{color: 'white'}}/>
                </IconButton>
              )}
            </EditDialogTitle>

            <DialogContent {...contentProps} className={clsx(contentProps?.className,dialogContentStyles)}>{children}</DialogContent>
            <DialogActions>
              <GreyOutlinedButton
                test-id={testId + '-avbryt-btn'}
                onClick={() => onCancel()}
              >
                <ButtonTypography>Avbryt</ButtonTypography>
              </GreyOutlinedButton>

              <PrimaryContainedButton
                onClick={onSave}
                disabled={!canSave}
                test-id={testId + '-lagre-btn'}
                hidden={hideSaveBtn}
              >
                <ButtonTypography>{lagreBtnTitle}</ButtonTypography>
              </PrimaryContainedButton>
            </DialogActions></>
        </ResizableBox>
      </Dialog>
    </>
  );
};
